import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import { buildMonthlyAnalytics } from "./utils/buildMonthlyAnalytics";
import { sampleMonthlyReviews } from "./data/sampleMonthlyReviews";
import jsPDF from "jspdf";

const Bar = ({ value = 0, color = "bg-cyan-400" }) => {
    return (
        <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden">
            <div
                className={`h-full rounded-full ${color}`}
                style={{ width: `${value}%` }}
            />
        </div>
    );
};

const PDMonthly = ({useSample, isActiveSub, startDate, endDate}) => {
    const authState = useSelector((state) => state.authReducer);

    const [fetchData] = useFetch();
    const [loading, setLoading] = useState(false);

    const userId = authState?.user?._id;
    const userCode = authState?.user?.think5whyscode;
    const [activeMonth, setActiveMonth] = useState(1);

    const [monthlyData, setMonthlyData] = useState({
        score: 0,
        stats: {
            total: 0,
            done: 0,
            planned: 0,
            cancelled: 0,
        },
        trend: [0, 0, 0, 0, 0, 0, 0],
        bestDay: "-",
        weakDay: "-",
        objectives: {},
        signals: {
            focus: 0,
            consistency: 0,
            discipline: 0,
            resilience: 0,
        },
        weaknesses: [],
        roach: {
            avgScore: 0,
            type: "Unknown Survivor",
        },
        insights: [],
    });

    // const d = monthlyData;
    const sampleMonthData = sampleMonthlyReviews.find( m => m.monthNumber === activeMonth) || null;
    const d = useSample ? sampleMonthData: monthlyData;

    const fetchMonthlyReview = async (range) => {
        try {
            setLoading(true);

            const config = {
                url: "/pd/my/todoroaches/raw",
                method: "post",
                data: {
                    userId,
                    startDate: range.start,
                    endDate: range.end,
                },
                headers: {
                    Authorization: authState.token,
                },
            };

            const response = await fetchData(config, { silent: true });
            if (response?.status) {
                const rawTodos = response?.todos || [];
                const rawRoach = response?.roachEntries || [];
                const monthly = buildMonthlyAnalytics({
                    todos: rawTodos,
                    roachEntries: rawRoach,
                });
                setMonthlyData(monthly);
            }
        } finally {
            setLoading(false);
        }
    };

    // useEffect(() => {
    //     if (userId && activeRange) {
    //         fetchMonthlyReview(activeRange);
    //     }
    // }, [userId, activeMonth, startDate, endDate]);

    useEffect(() => {

        if (useSample) return;

        if (userId && activeRange) {
            fetchMonthlyReview(activeRange);
        }

    }, [userId, activeMonth, startDate, endDate,useSample]);


    const getMonthRanges = () => {
        if (!startDate || !endDate) return [];

        const start = new Date(startDate);
        const end = new Date(endDate);

        const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        const chunk = Math.ceil(totalDays / 3);

        return [1, 2, 3].map((m, i) => {
            const from = new Date(start);
            from.setDate(start.getDate() + i * chunk);

            const to = new Date(start);
            to.setDate(start.getDate() + (i + 1) * chunk);

            if (to > end) to.setTime(end.getTime());

            return {
                label: `Month ${m}`,
                start: from.toISOString(),
                end: to.toISOString(),
            };
        });
    };

    const monthRanges = useMemo(() => getMonthRanges(), [startDate, endDate]);
    const activeRange = monthRanges[activeMonth - 1];
    
    const handleShareMonthlyReport = () => {
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        let y = 20;

        const addSection = (title) => {
            if (y > pageHeight - 25) {
                pdf.addPage();
                y = 20;
            }
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(14);
            pdf.text(title, 15, y);
            y += 8;
        };

        const addText = (text) => {
            if (!text) return;
            const lines = pdf.splitTextToSize(
                String(text),
                180
            );
            if (y + lines.length * 5 > pageHeight - 20) {
                pdf.addPage();
                y = 20;
            }
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(10);
            pdf.text(lines, 15, y);

            y += lines.length * 5 + 3;
        };

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(22);
        pdf.text(
            "PurpleDoor Monthly Report",
            pageWidth / 2,
            25,
            { align: "center" }
        );

        pdf.setFontSize(11);
        pdf.text(
            `Generated: ${new Date().toLocaleString()}`,
            pageWidth / 2,
            35,
            { align: "center" }
        );

        y += 20;
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(`Name: ${authState?.user?.name}`, 15, y);

        y += 10;

        addSection("Monthly Overview");
        addText(`Monthly Productivity Score: ${d?.score || 0}/100`);
        addText(
            "Track your monthly execution, productivity and behavioral growth."
        );

        addSection("Task Statistics");
        addText(`Total Tasks: ${d?.stats?.total || 0}`);
        addText(`Completed Tasks: ${d?.stats?.done || 0}`);
        addText(`Planned Tasks: ${d?.stats?.planned || 0}`);
        addText(`Cancelled Tasks: ${d?.stats?.cancelled || 0}`);

        addSection("Execution Pattern");
        addText(
            `Best Day: ${d?.bestDay || "-"}`
        );
        addText(
            "Highest execution and productivity performance."
        );
        addText(
            `Weak Day: ${d?.weakDay || "-"}`
        );
        addText(
            "Lowest behavioral consistency and execution."
        );

        addSection("Behavior Signals");
        addText(
            `Focus Strength: ${d?.signals?.focus || 0}%`
        );
        addText(
            `Consistency: ${d?.signals?.consistency || 0}%`
        );
        addText(
            `Discipline: ${d?.signals?.discipline || 0}%`
        );
        addText(
            `Resilience: ${d?.signals?.resilience || 0}%`
        );

        addSection("Objective Distribution");
        Object.entries(
            d?.objectives || {}
        ).forEach(([key, value]) => {
            const percent = Math.round(
                (value /
                    Math.max(
                        d?.stats?.total || 1,
                        1
                    )) *
                100
            );
            addText(
                `${key}: ${value} activities (${percent}%)`
            );
        });

        addSection("Survivor Profile");
        addText(
            `Profile Type: ${
                d?.roach?.type ||
                "Unknown Survivor"
            }`
        );
        addText(
            `Average Survivor Score: ${
                d?.roach?.avgScore || 0
            }/9`
        );
        addText(
            "Behavioral survival pattern observed throughout the month."
        );

        addSection("Weak Areas");
        if (d?.weaknesses?.length) {
            d.weaknesses.forEach((item) => {
                addText(`• ${item}`);
            });
        } else {
            addText(
                "No significant weakness detected."
            );
        }

        addSection("Monthly Insights");
        if (d?.insights?.length) {
            d.insights.forEach((item) => {
                addText(`• ${item}`);
            });
        } else {
            addText(
                "No insights generated."
            );
        }

        addSection("Monthly Outcome Summary");
        addText(
            `Overall Productivity Score: ${
                d?.score || 0
            }/100`
        );
        addText(
            `Best Performing Day: ${
                d?.bestDay || "-"
            }`
        );
        addText(
            `Primary Survivor Type: ${
                d?.roach?.type ||
                "Unknown Survivor"
            }`
        );
        addText(
            "Generated by Think5Whys PurpleDoor Monthly Behaviour Review Engine."
        );

        pdf.save(
            `PurpleDoor-Monthly-Report-${
                new Date()
                    .toISOString()
                    .split("T")[0]
            }.pdf`
        );
    };


    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-orange-400 mt-2">
            {useSample && (
                <div className="space-y-6 bg-gray-700 p-6 rounded-xl text-white border-t-2 border-blue-600 mt-2">
                    📦 You are viewing SAMPLE DASHBOARD DATA (for demo purpose)
                </div>
            )}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
                <div className="flex gap-2 mb-3">
                    {/* {monthRanges.map((m, i) => ( */}
                    {(useSample ? [1,2,3] : monthRanges).map((m, i) => (
                        <button
                            key={i}
                            onClick={() => setActiveMonth(i + 1)}
                            className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all ${
                                activeMonth === i + 1
                                    ? "bg-purple-500 text-white"
                                    : "bg-gray-800 text-gray-300 border-gray-700"
                            }`}
                        >
                            Month {i + 1}
                        </button>
                    ))}
                </div>
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-lg font-bold">
                            📆 Monthly Overview
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                            Track your monthly execution & behavioral growth
                        </div>
                    </div>
                    <div className="bg-purple-500/20 text-purple-300 px-3 py-2 rounded-xl text-sm font-bold">
                        Monthly Review
                    </div>
                </div>

                <div className="mt-5">
                    <div className="flex items-end gap-2">
                        <div className="text-4xl font-black text-white">
                            {d?.score || 0}
                        </div>
                        <div className="text-sm text-gray-400 pb-1">
                            /100 Productivity
                        </div>
                    </div>

                    <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden mt-4">
                        <div
                            className="h-full bg-purple-400 rounded-full transition-all"
                            style={{
                                width: `${d?.score || 0}%`,
                            }}
                        />
                    </div>

                    <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                        <span>Low Output</span>
                        <span>High Output</span>
                    </div>
                </div>
            </div>

            {/* TASK STATS */}
            <div className="grid grid-cols-4 gap-2">
                <div className="bg-[#1f2937] rounded-xl p-3 border border-gray-800">
                    <div className="text-[10px] text-gray-400">
                        📋 Total
                    </div>
                    <div className="text-lg font-black mt-1">
                        {d?.stats?.total || 0}
                    </div>
                </div>

                <div className="bg-[#1f2937] rounded-xl p-3 border border-gray-800">
                    <div className="text-[10px] text-gray-400">
                        ✅ Done
                    </div>
                    <div className="text-lg font-black text-green-300 mt-1">
                        {d?.stats?.done || 0}
                    </div>
                </div>

                <div className="bg-[#1f2937] rounded-xl p-3 border border-gray-800">
                    <div className="text-[10px] text-gray-400">
                        ⏳ Planned
                    </div>
                    <div className="text-lg font-black text-yellow-300 mt-1">
                        {d?.stats?.planned || 0}
                    </div>
                </div>

                <div className="bg-[#1f2937] rounded-xl p-3 border border-gray-800">
                    <div className="text-[10px] text-gray-400">
                        ❌ Cancelled
                    </div>
                    <div className="text-lg font-black text-red-300 mt-1">
                        {d?.stats?.cancelled || 0}
                    </div>
                </div>
            </div>

            {/* BEST + WEAK DAY */}
            <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">
                    <div className="text-xs text-gray-400">
                        🔥 Best Day
                    </div>
                    <div className="text-xl font-black mt-2 text-green-300">
                        {d?.bestDay || "-"}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                        Highest execution pattern
                    </div>
                </div>

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">
                    <div className="text-xs text-gray-400">
                        ⚠️ Weak Day
                    </div>
                    <div className="text-xl font-black mt-2 text-red-300">
                        {d?.weakDay || "-"}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-1">
                        Lowest behavioral consistency
                    </div>
                </div>
            </div>

            {/* SIGNALS */}
            <div className="grid grid-cols-2 gap-2">
                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">
                    <div className="text-xs text-gray-400">
                        🎯 Focus
                    </div>
                    <div className="text-2xl font-black text-cyan-300 mt-2">
                        {d?.signals?.focus || 0}%
                    </div>
                </div>

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">
                    <div className="text-xs text-gray-400">
                        ⚡ Consistency
                    </div>
                    <div className="text-2xl font-black text-green-300 mt-2">
                        {d?.signals?.consistency || 0}%
                    </div>
                </div>

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">
                    <div className="text-xs text-gray-400">
                        🧠 Discipline
                    </div>
                    <div className="text-2xl font-black text-yellow-300 mt-2">
                        {d?.signals?.discipline || 0}%
                    </div>
                </div>

                <div className="bg-[#1f2937] rounded-xl p-4 border border-gray-800">
                    <div className="text-xs text-gray-400">
                        🪳 Resilience
                    </div>
                    <div className="text-2xl font-black text-pink-300 mt-2">
                        {d?.signals?.resilience || 0}%
                    </div>
                </div>
            </div>

            {/* OBJECTIVES */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
                <div className="text-sm font-bold mb-4">
                    🎯 Objective Distribution
                </div>
                <div className="space-y-3">
                    {Object.entries(d?.objectives || {}).map(([key, value]) => {
                        const percentage = Math.round(
                            (value / Math.max(d?.stats?.total || 1, 1)) * 100
                        );
                        return (
                            <div key={key}>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="text-xs text-gray-300">
                                        {key}
                                    </div>
                                    <div className="text-xs font-bold text-white">
                                        {value}
                                    </div>
                                </div>
                                <div className="w-full h-3 bg-gray-800 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-purple-400"
                                        style={{
                                            width: `${percentage}%`,
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ROACH PROFILE */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
                <div className="flex items-center justify-between">
                    <div className="text-sm font-bold">
                        🪳 Survivor Profile
                    </div>
                    <div className="text-xs text-pink-300 font-bold">
                        {d?.roach?.avgScore || 0}/9
                    </div>
                </div>
                <div className="mt-3 text-2xl font-black text-pink-300">
                    {d?.roach?.type || "Unknown Survivor"}
                </div>
                <div className="text-xs text-gray-400 mt-2">
                    Behavioral survival pattern across the month.
                </div>
            </div>

            {/* WEAKNESSES */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
                <div className="text-sm font-bold mb-4">
                    ⚠️ Weak Areas
                </div>
                <div className="flex flex-wrap gap-2">
                    {(d?.weaknesses || []).map((w, i) => (
                        <span
                            key={i}
                            className="bg-red-500/20 text-red-300 px-3 py-2 rounded-lg text-xs border border-red-500/20"
                        >
                            {w}
                        </span>
                    ))}
                </div>
            </div>

            {/* INSIGHTS */}
            <div className="bg-[#1f2937] rounded-2xl p-4 border border-gray-800">
                <div className="text-sm font-bold mb-4">
                    🧠 Monthly Insights
                </div>
                <div className="space-y-2">
                    {(d?.insights || []).map((insight, i) => (
                        <div
                            key={i}
                            className="bg-[#111827] rounded-xl p-3"
                        >
                            <div className="text-xs text-cyan-400 font-bold">
                                Insight {i + 1}
                            </div>
                            <div className="text-xs text-gray-300 mt-1">
                                {insight}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex flex-row gap-2">
                <button className="w-48 bg-cyan-500 hover:bg-cyan-400 text-black py-1 rounded-xl text-xs transition-all">
                    🎯 Start Your Month Again
                </button>
                <button
                    onClick={handleShareMonthlyReport}
                    className="w-48 bg-green-600 hover:bg-green-500 transition-all py-1 rounded-xl text-xs"
                >
                    📄 Share Monthly Report
                </button>
            </div>
        </div>
    );
};

export default PDMonthly;